import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestNotificationRefresh } from './notification_events';

export const formatNotificationDate = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};

export const formatNotificationAddress = value => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return [value.street, value.street2, value.city, value.state || value.region, value.zip || value.postal, value.country]
    .filter(Boolean)
    .join(', ');
};

export const NotificationCard = ({ notification, onOpen }) => {
  const address = formatNotificationAddress(notification.location_address);
  return (
  <button
    type="button"
    className={`notificationCard ${notification.read_at ? '' : 'notificationUnread'}`}
    onClick={() => onOpen(notification.notification_id)}
  >
    <span className="notificationCardHeader">
      <strong>{notification.title || 'Notification'}</strong>
      {!notification.read_at && <span className="notificationUnreadDot" aria-label="Unread" />}
    </span>
    {notification.location_code && <span className="notificationLocationCode">Location: {notification.location_code}</span>}
    {address && <span className="notificationLocationAddress">{address}</span>}
    <span className="notificationBody">{notification.body}</span>
    <time className="notificationDate" dateTime={notification.created_at || ''}>{formatNotificationDate(notification.created_at)}</time>
  </button>
  );
};

export const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const pageSize = 10;

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const response = await fetch('/apinotifications?limit=100');
      if (!response.ok) throw new Error('Unable to load notifications');
      const data = await response.json();
      setNotifications(Array.isArray(data) ? data : []);
      setStatus('ready');
    } catch (loadError) {
      setError(loadError.message);
      setStatus('error');
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter]);

  const unreadCount = notifications.filter(item => !item.read_at).length;
  const filtered = useMemo(() => filter === 'unread' ? notifications.filter(item => !item.read_at) : notifications, [filter, notifications]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  const markAllRead = async () => {
    try {
      const response = await fetch('/apinotifications/read-all', { method: 'POST' });
      if (!response.ok) throw new Error('Unable to mark notifications read');
      setNotifications(current => current.map(item => ({ ...item, read_at: item.read_at || true })));
      requestNotificationRefresh();
    } catch (markError) {
      setError(markError.message);
    }
  };

  return (
    <div className="notificationsPage pageSection">
      <div className="notificationsPanel">
        <div className="notificationsHeader">
          <div><h1>Notifications</h1><p>Updates about your service requests.</p></div>
          <button type="button" className="secondaryButton" onClick={markAllRead} disabled={!unreadCount}>Mark all read</button>
        </div>
        <div className="notificationFilters" role="tablist" aria-label="Notification filters">
          <button type="button" role="tab" aria-selected={filter === 'all'} className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>All <span>{notifications.length}</span></button>
          <button type="button" role="tab" aria-selected={filter === 'unread'} className={filter === 'unread' ? 'active' : ''} onClick={() => setFilter('unread')}>Unread <span>{unreadCount}</span></button>
        </div>
        {status === 'loading' && <div className="notificationEmpty" role="status">Loading notifications…</div>}
        {status === 'error' && <div className="notificationEmpty notificationError" role="alert">{error} <button type="button" onClick={load}>Try again</button></div>}
        {status === 'ready' && visible.length === 0 && <div className="notificationEmpty">{filter === 'unread' ? 'You have no unread notifications.' : 'You have no notifications yet.'}</div>}
        {status === 'ready' && visible.length > 0 && <div className="notificationList">{visible.map(item => <NotificationCard key={item.notification_id} notification={item} onOpen={id => navigate(`/notifications/${id}/open`)} />)}</div>}
        {status === 'ready' && filtered.length > pageSize && (
          <div className="notificationPagination">
            <button type="button" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Previous</button>
            <span>Page {page} of {pageCount}</span>
            <button type="button" disabled={page === pageCount} onClick={() => setPage(value => value + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};
